/** Prompt body, collapsible detail rows, comments and the horizontal rail. */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { CommentNode } from '@promptvault/shared';
import { compactNumber, timeAgo } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { haptic } from '../hooks/useTg';
import { Icon } from './Icon';
import { Avatar, IconButton } from './primitives';

/**
 * The prompt body in a surface card with a trailing copy affordance. Long text collapses at
 * roughly ten lines; the toggle only appears when the text actually overflows, measured after
 * layout rather than guessed from character count (a Farsi prompt and an English one of the
 * same length wrap very differently).
 */
export function PromptTextBlock({
  label,
  body,
  onCopy,
  mono = false,
}: {
  label: string;
  body: string;
  onCopy?: () => void;
  mono?: boolean;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [body]);

  return (
    <div className="overflow-hidden rounded-card bg-surface">
      <div className="flex items-center justify-between gap-2 px-3.5 pt-3">
        <span className="text-footnote font-semibold uppercase tracking-wide text-hint">
          {label}
        </span>
        {onCopy && (
          <IconButton
            name="copy"
            label={t.copyPrompt}
            testId="copy-prompt"
            onClick={onCopy}
            size={18}
            className="-me-2 text-accent"
          />
        )}
      </div>
      {/* Padding lives on the wrapper: -webkit-line-clamp clips at the content box, so any
          padding on the clamped element itself leaks a sliver of the next line. */}
      <div className="px-3.5 pb-3.5 pt-2">
        <p
          ref={textRef}
          data-testid="prompt-body"
          /**
           * `auto`, not `ltr`.
           *
           * Hardcoded `ltr` is right for the English prompt that was on screen when it was
           * written and wrong for every Farsi one — the library has both. `auto` takes the
           * direction from the text's own first strong character, and because that sets the
           * element's `direction`, `text-start` then resolves against the paragraph rather than
           * against the page. `unicode-bidi: plaintext` (the `bidi-plaintext` class used on
           * titles) gets the character *ordering* right but leaves alignment following the page,
           * which is what left English paragraphs right-aligned with a ragged left edge.
           */
          dir="auto"
          className={`select-text whitespace-pre-wrap text-start text-callout leading-relaxed text-text ${
            mono ? 'font-mono' : ''
          }`}
          style={
            expanded
              ? undefined
              : {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 10,
                  overflow: 'hidden',
                }
          }
        >
          {body}
        </p>
      </div>
      {overflows && (
        <button
          type="button"
          onClick={() => {
            haptic('light');
            setExpanded((v) => !v);
          }}
          className="hairline-t w-full px-3.5 py-2.5 text-start text-footnote font-medium text-accent"
        >
          {expanded ? t.showLess : t.showMore}
        </button>
      )}
    </div>
  );
}

/** Collapsible surface row — negative prompt, "how to use". */
export function DisclosureRow({
  label,
  children,
  defaultOpen = false,
}: {
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-card bg-surface">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          haptic('light');
          setOpen((v) => !v);
        }}
        className="flex min-h-tap w-full items-center gap-2.5 px-3.5 py-3 text-start"
      >
        <span className="flex-1 text-body text-text">{label}</span>
        <span
          className="flex-none text-chevron transition-transform duration-200 ease-pv"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <Icon name="chevronDown" size={19} strokeWidth={2.2} />
        </span>
      </button>
      {open && (
        <div
          dir="auto"
          className="hairline-t select-text whitespace-pre-wrap px-3.5 pb-3.5 pt-3 text-start text-subhead leading-relaxed text-hint"
        >
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Comments ────────────────────────────────────────────────────────────── */

export function CommentItem({
  comment,
  onLike,
  onReply,
  depth = 0,
}: {
  comment: CommentNode;
  onLike: (comment: CommentNode) => void;
  onReply?: (comment: CommentNode) => void;
  depth?: number;
}) {
  const { t, lang } = useI18n();

  return (
    <div style={{ paddingInlineStart: depth > 0 ? 28 : 0 }}>
      <div className="flex gap-2.5 rounded-card bg-surface px-3 pt-3">
        <Avatar name={comment.author.name} photoUrl={comment.author.photoUrl} size={34} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-baseline gap-2">
            <span className="bidi-plaintext truncate text-subhead font-semibold text-accent">
              {comment.author.name}
            </span>
            <span className="flex-none text-caption text-hint">
              {timeAgo(comment.createdAt, lang)}
            </span>
          </div>
          {/* plaintext: an English comment inside an RTL page must keep its own direction,
              or trailing punctuation jumps to the wrong end of the line. */}
          <p dir="auto" className="text-balance text-start text-subhead leading-relaxed text-text">
            {comment.body}
          </p>
          <div className="-mx-2.5 -mb-px flex items-center gap-1.5">
            {onReply && (
              <button
                type="button"
                onClick={() => {
                  haptic('light');
                  onReply(comment);
                }}
                className="flex min-h-tap items-center gap-1.5 rounded-thumb px-2.5 text-footnote text-hint transition-colors duration-fast ease-pv active:bg-pressed-soft"
              >
                <Icon name="reply" size={14} />
                {t.reply}
              </button>
            )}
            <button
              type="button"
              aria-pressed={comment.liked ?? false}
              onClick={() => {
                haptic('light');
                onLike(comment);
              }}
              className={`flex min-h-tap items-center gap-1.5 rounded-thumb px-2.5 text-footnote transition-colors duration-fast ease-pv active:bg-pressed-soft ${
                comment.liked ? 'text-destructive' : 'text-hint'
              }`}
            >
              <Icon name="heart" size={14} fill={comment.liked ?? false} />
              {compactNumber(comment.likeCount, lang)}
            </button>
          </div>
        </div>
      </div>

      {comment.replies?.map((reply) => (
        <div key={reply.id} className="pt-2">
          <CommentItem comment={reply} onLike={onLike} depth={depth + 1} />
        </div>
      ))}
    </div>
  );
}

/** Inline composer that promotes to the bottom sheet on focus. */
export function CommentComposer({
  value,
  onChange,
  onSend,
  placeholder,
  disabled = false,
}: {
  value: string;
  onChange: (next: string) => void;
  onSend: () => void;
  placeholder: string;
  disabled?: boolean;
}) {
  const canSend = value.trim().length > 0 && !disabled;
  return (
    <div className="flex items-center gap-2 rounded-card bg-surface py-2 pe-2 ps-3">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSend) onSend();
        }}
        placeholder={placeholder}
        maxLength={2000}
        enterKeyHint="send"
        className="h-[34px] min-w-0 flex-1 border-0 bg-transparent text-callout text-text outline-none placeholder:text-hint"
      />
      <button
        type="button"
        aria-label={placeholder}
        disabled={!canSend}
        onClick={() => {
          haptic('light');
          onSend();
        }}
        className={`flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full transition-[background,color] duration-200 ease-pv ${
          canSend ? 'bg-button text-button-text' : 'bg-fill text-chevron'
        }`}
      >
        <Icon name="send" size={17} />
      </button>
    </div>
  );
}

/* ── Layout helpers ──────────────────────────────────────────────────────── */

export function SectionTitle({
  title,
  action,
}: {
  title: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="text-headline font-semibold text-text">{title}</h2>
      {action && (
        <button
          type="button"
          onClick={() => {
            haptic('light');
            action.onClick();
          }}
          className="flex-none whitespace-nowrap text-subhead text-accent"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Horizontally scrolling rail that bleeds to the screen edges. */
/**
 * A horizontal carousel that feels native, because it *is* native.
 *
 * The temptation with "make it feel like iOS" is a transform-driven carousel with a hand-written
 * spring. That is the wrong call inside a Telegram WebView: the platform's own overflow scrolling
 * already gives physical deceleration, an interruptible fling (a new swipe during deceleration
 * takes over immediately), and rubber-band at the ends — and a JS implementation replaces all
 * three with an approximation that fights the compositor. What the old version actually lacked was
 * snapping, a visible peek, and enough items behind it.
 *
 * - **`x mandatory`, not `proximity`.** Proximity leaves cards parked mid-gutter, which is what
 *   made this read as a scrolling div rather than a carousel.
 * - **`scroll-padding-inline-start`** so a snapped card lands on the gutter rather than flush to
 *   the viewport edge. `inline-start` rather than `left` is what makes it correct in Farsi, where
 *   the rail scrolls the other way.
 * - **The peek is the affordance.** At 390px with a 16px gutter, two 160px cards and an 8px gap
 *   leave ~46px of the third card showing. That sliver is the single most important signal that
 *   there is more to the right; without it users treat a rail as everything it contains.
 * - **`overscroll-behavior-x: contain`** so swiping past the end does not hand the gesture to the
 *   page and trigger Telegram's swipe-to-close.
 */
export function Rail({
  children,
  itemWidth = 160,
  snap = true,
  testId,
}: {
  children: ReactNode[];
  itemWidth?: number;
  snap?: boolean;
  testId?: string;
}) {
  return (
    <div
      className="sc -mx-gutter flex gap-2 overflow-x-auto px-gutter"
      data-testid={testId}
      style={{
        scrollSnapType: snap ? 'x mandatory' : undefined,
        scrollPaddingInlineStart: 'var(--pv-gutter, 16px)',
        overscrollBehaviorX: 'contain',
        // iOS momentum. Harmless where it is already the default.
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {children.map((child, i) => (
        <div
          key={i}
          className="flex-none"
          style={{ width: itemWidth, scrollSnapAlign: snap ? 'start' : undefined }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
